import { confirmPatient, parseBirthDate, type CaseForPharmacy } from "@omony/domain";
import { Banner, Button, Checkbox, KeyValueList, TextField } from "@omony/ui";
import { ArrowRight, Pencil } from "lucide-react";
import { useState } from "react";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { EMPTY } from "../../shared/format.js";
import { useCaseAction } from "../data.js";
import { Spacer, StepFrame, useT } from "./common.js";
import styles from "./flow.module.css";

/** Bestätigung (A11): Daten Punkt für Punkt abgleichen, KVNR wiederholen (Fail-Safe) */
export function ConfirmStep({ c, onEdit }: { c: CaseForPharmacy; onEdit: () => void }) {
  const t = useT();
  const p = c.patient!;
  const [kvnr, setKvnr] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [local, setLocal] = useState<string | null>(null);
  const action = useCaseAction<{ version: number; kvnrRepeat: string; confirmed: boolean }>(
    c.id,
    "patient/confirm",
  );
  const dob = parseBirthDate(p.geburtsdatum);

  const submit = () => {
    const r = confirmPatient(p, kvnr, confirmed);
    setLocal(r.ok ? null : r.error);
    if (r.ok) action.mutate({ version: c.version, kvnrRepeat: kvnr, confirmed });
  };

  return (
    <StepFrame
      eyebrow={t("console.step.bestaetigen")}
      title={t("console.confirm.title")}
      actions={
        <>
          <Button variant="secondary" icon={<Pencil />} onPress={onEdit}>
            {t("console.confirm.edit")}
          </Button>
          <Spacer />
          <Button
            size="lg"
            icon={<ArrowRight className="omony-mirror" />}
            onPress={submit}
            isDisabled={action.isPending}
          >
            {t("console.confirm.submit")}
          </Button>
        </>
      }
    >
      <Banner tone="warning">{t("console.confirm.warning")}</Banner>
      <KeyValueList
        items={[
          {
            key: "name",
            label: t("console.confirm.name"),
            value: dob
              ? t("console.confirm.nameAge", { name: `${p.name}, ${p.vorname}`, age: dob.age })
              : `${p.name}, ${p.vorname}`,
          },
          { key: "addr", label: t("console.confirm.address"), value: `${p.strasse}, ${p.plz} ${p.ort}` },
          { key: "kvnr", label: t("console.patient.field.kvnr"), value: p.kvnr, ltr: true },
          { key: "dob", label: t("console.patient.field.geburtsdatum"), value: p.geburtsdatum, ltr: true },
          { key: "kk", label: t("console.patient.field.krankenkasse"), value: p.krankenkasse },
          { key: "ik", label: t("console.patient.field.ik"), value: p.ik || EMPTY, ltr: true },
          { key: "tel", label: t("console.patient.field.telefon"), value: p.telefon || EMPTY, ltr: true },
          {
            key: "ins",
            label: t("console.patient.insurance"),
            value: t(`console.patient.${c.insuranceType}`),
          },
          {
            key: "doc",
            label: t("console.patient.doctorSource"),
            value: t(`console.patient.doctor.${c.doctorSource}`),
          },
          {
            key: "elig",
            label: t("console.confirm.eligibility"),
            value: c.eligibility.length ? c.eligibility.map((e) => `${e})`).join(", ") : EMPTY,
            ltr: true,
          },
          {
            key: "src",
            label: t("console.confirm.source"),
            value: t(`console.confirm.source.${c.patientCaptureSource ?? "manuell"}`),
          },
          ...(c.knownPatient
            ? [{ key: "known", label: t("console.patient.known"), value: t("common.yes") }]
            : []),
        ]}
      />
      <div className={styles.grid2}>
        <TextField
          label={t("console.confirm.kvnrRepeat")}
          description={t("console.confirm.kvnrRepeatHint")}
          value={kvnr}
          onChange={(v) => {
            setKvnr(v.toUpperCase());
            setLocal(null);
          }}
          code
          autoComplete="off"
          errorMessage={local === "kvnr_mismatch" ? t("error.kvnr_mismatch") : undefined}
        />
      </div>
      <Checkbox
        card
        isSelected={confirmed}
        onChange={(v) => {
          setConfirmed(v);
          setLocal(null);
        }}
      >
        {t("console.confirm.check")}
      </Checkbox>
      {local === "confirmation_required" ? (
        <Banner tone="danger">{t("error.confirmation_required")}</Banner>
      ) : null}
      <ErrorBanner error={action.error} />
    </StepFrame>
  );
}
